import debounce from 'lodash-es/debounce';
import { action, observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';

// Components
const iconDropdownArrow = require('assets/dropdown arrow.svg');

// Styles
require('./DropdownContainer.css');
const c = 'comp-dropdown-';

// Types
type TypeAlignOptions = 'default' | 'left' | 'center' | 'right';

// Interfaces
interface IListEvents {
    window: Array<{
        type: any;
        handler: any;
    }>;
}

// Props
interface IDropdownContainer {
    className?: string;
    minWidth?: number;
    maxWidth?: number;
    maxHeight?: number;
    align?: TypeAlignOptions;
    label: string;
    value?: string;
}

// Component
@observer
export class DropdownContainer extends React.Component<IDropdownContainer, {}> {
    static get defaultProps(): IDropdownContainer {
        return {
            className: '',
            minWidth: 0,
            maxWidth: 0,
            maxHeight: 0,
            align: 'left',
            label: '',
            value: '',
        };
    }

    constructor(props: IDropdownContainer) {
        super(props);

        this.windowResizeDebounced = debounce(this.windowResizeDebounced, 256);
    }

    @observable public open: boolean = false;
    @observable public positionOnTop: boolean = false;
    @observable public positionMarginLeft: number = 0;
    @observable public positionMarginTop: number = 0;
    @observable public maxHeight: number = 440;

    private openEvents: IListEvents = {
        window: [],
    };

    public componentWillUnmount() {
        // Clear component's events
        this.removeOpenEvents();
    }

    @action public delayedWindowResize = (e: React.UIEvent<Window>) => {
        this.windowResizeDebounced(e);
    };

    public addOpenEvents() {
        // Create window click event
        const windowClick = this.windowClicked.bind(this);
        this.openEvents.window.push({ type: 'click', handler: windowClick });
        window.addEventListener('click', windowClick, false);

        // Create window key up event
        const windowKeyUp = this.windowKeyedUp.bind(this);
        this.openEvents.window.push({ type: 'keyup', handler: windowKeyUp });
        window.addEventListener('keyup', windowKeyUp, false);

        // Create window resize event
        const windowResize = this.windowResize.bind(this);
        this.openEvents.window.push({ type: 'resize', handler: windowResize });
        window.addEventListener('resize', windowResize, false);
    }

    public removeOpenEvents() {
        // Remove window events
        this.openEvents.window.map((evt: any) => {
            window.removeEventListener(evt.type, evt.handler);
        });
    }

    @action public windowClicked = (e: React.MouseEvent<Window>) => {
        this.closeDropdown();
    };

    @action public windowKeyedUp = (
        e: React.KeyboardEvent<HTMLInputElement>
    ) => {
        // Base behavior on the logged key
        switch (e.keyCode) {
            // ESC key
            case 27:
                this.closeDropdown();
                break;

            default:
                break;
        }
    };

    @action public windowResize = (e: React.UIEvent<Window>) => {
        this.delayedWindowResize(e);
    };

    @action public windowResizeDebounced(e: React.UIEvent<Window>) {
        this.positionAndResizeDropdown();
    }

    @action public closeDropdown() {
        // Change state to closed
        this.open = false;
        this.positionMarginLeft = 0;
        this.positionMarginTop = 0;

        // Remove events associated with open dropdown
        this.removeOpenEvents();
    }

    @action public openDropdown() {
        this.addOpenEvents();
        this.positionAndResizeDropdown();
    }

    @action public handleLabelClick = (e: React.MouseEvent<HTMLDivElement>) => {
        // Close
        if (this.open === true) {
            // Set closed state
            this.closeDropdown();
            // Open
        } else {
            // Add events related to open dropdown
            this.addOpenEvents();

            // Position dropdown
            this.positionAndResizeDropdown();
        }

        // Stop propagation
        e.stopPropagation();
    };

    @action public positionAndResizeDropdown() {
        // Get dimensions needed for calculations
        const windowHeight = window.innerHeight;
        const windowWidth = window.innerWidth;
        const labelPosition = this.label
            ? this.label.getBoundingClientRect()
            : { top: 0, left: 0 };
        const labelWidth = this.label ? this.label.offsetWidth : 0;
        const labelHeight = this.label ? this.label.offsetHeight : 0;
        const dropdownWidth = labelWidth > 192 ? labelWidth : 192;

        // Determine top or bottom position
        let positionOnTop =
            windowHeight * 0.8 < labelPosition.top ? true : false;

        // Determine left margin
        let positionMarginLeft = 0;
        if (labelWidth < dropdownWidth) {
            switch (this.props.align) {
                case 'right':
                    positionMarginLeft = -(dropdownWidth - labelWidth);
                    break;

                case 'center':
                    positionMarginLeft = -((dropdownWidth - labelWidth) / 2);
                    break;

                default:
                    break;
            }
        }
        const rightDropdownEdgePosition =
            labelPosition.left + dropdownWidth + positionMarginLeft;
        if (windowWidth < rightDropdownEdgePosition) {
            positionMarginLeft =
                positionMarginLeft - (rightDropdownEdgePosition - windowWidth);
        }

        // Determine top margin
        const positionMarginTop =
            positionOnTop === true ? -labelHeight - 8 : labelHeight + 8;

        // Calculate max height
        let maxHeight =
            typeof this.props.maxHeight !== 'undefined'
                ? this.props.maxHeight
                : 0;

        const maxHeightOnTop = labelPosition.top + positionMarginTop - 12 - 64;
        // 64 above is height of the top bar

        const maxHeightOnBottom =
            windowHeight - (labelPosition.top + positionMarginTop + 12);

        positionOnTop =
            maxHeightOnBottom * 1.2 < maxHeightOnTop ? true : positionOnTop;
        maxHeight = positionOnTop
            ? maxHeight <= 0 || maxHeightOnTop < maxHeight
                ? maxHeightOnTop
                : maxHeight
            : maxHeight <= 0 || maxHeightOnBottom < maxHeight
            ? maxHeightOnBottom
            : maxHeight;

        // Set open state
        this.open = true;
        this.positionOnTop = positionOnTop;
        this.positionMarginLeft = positionMarginLeft;
        this.positionMarginTop = positionMarginTop;
        this.maxHeight = maxHeight;
    }

    public handleDropdownGroupClick = (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
    };

    public render() {
        // Prepare class name
        let dropdownClassName = `${c}container`;
        dropdownClassName += this.open === true ? ` ${c}open` : '';
        dropdownClassName +=
            this.props.className !== '' ? ' ' + this.props.className : '';
        dropdownClassName += ' ' + this.props.align;

        // Label class name
        const labelClassName = `${c}label`;

        // Dropdown group class name
        let dropdownGroupClassName = `${c}group`;
        dropdownGroupClassName += this.positionOnTop === true ? ' top' : '';

        // Dropdown group style
        const dropdownGroupStyle = {
            marginLeft:
                this.positionMarginLeft !== 0
                    ? this.positionMarginLeft + 'px'
                    : undefined,
            marginTop:
                this.positionMarginTop > 0
                    ? this.positionMarginTop + 'px'
                    : undefined,
            marginBottom:
                this.positionMarginTop < 0
                    ? -this.positionMarginTop + 'px'
                    : undefined,
            maxWidth:
                typeof this.props.maxWidth !== 'undefined' &&
                this.props.maxWidth > 0
                    ? this.props.maxWidth + 'px'
                    : undefined,
            minWidth:
                typeof this.props.minWidth !== 'undefined' &&
                this.props.minWidth > 0
                    ? this.props.minWidth + 'px'
                    : undefined,
            maxHeight: this.maxHeight > 0 ? this.maxHeight + 'px' : undefined,
        };

        // Render
        return (
            <div className={dropdownClassName}>
                <div className={labelClassName} onClick={this.handleLabelClick}>
                    <p ref={this.referenceLabel}>
                        {this.props.label && <span>{this.props.label}</span>}
                        {this.props.value && (
                            <strong>{this.props.value}</strong>
                        )}
                    </p>
                    <img src={iconDropdownArrow} width={8} height={4} />
                </div>

                {typeof this.props.children !== 'undefined' &&
                    this.props.children && (
                        <div
                            style={dropdownGroupStyle}
                            className={dropdownGroupClassName}
                            onClick={this.handleDropdownGroupClick}
                        >
                            {this.open && this.props.children}
                        </div>
                    )}
            </div>
        );
    }

    private label: HTMLParagraphElement = null;
    private referenceLabel = (ref: HTMLParagraphElement) => (this.label = ref);
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./app/web/src/components/list/DropdownContainer.tsx